import React from "react";
import { GripVertical, Trash2 } from "lucide-react";
import { Input, Textarea } from "src/components/ui/Input";
import { faqLabelClass } from "./faqConstants";
import { useFaqStore } from "src/store/faqStore";

type FaqContentRowItemProps = {
  sectionIndex: number;
  rowIndex: number;
  isDragging: boolean;
  /** When set, row reorder uses @dnd-kit (handle only). */
  setNodeRef?: (node: HTMLElement | null) => void;
  sortableStyle?: React.CSSProperties;
  dragHandleProps?: Record<string, unknown>;
};

export const FaqContentRowItem: React.FC<FaqContentRowItemProps> = ({
  sectionIndex,
  rowIndex,
  isDragging,
  setNodeRef,
  sortableStyle,
  dragHandleProps,
}) => {
  const canManage = useFaqStore((s) => s.canManage);
  const disabled = !canManage;
  const row = useFaqStore((s) => s.form?.faq[sectionIndex]?.content[rowIndex]);
  const contentLen =
    useFaqStore((s) => s.form?.faq[sectionIndex]?.content.length) ?? 0;
  const patchContentItem = useFaqStore((s) => s.patchContentItem);
  const removeContentRow = useFaqStore((s) => s.removeContentRow);

  if (!row) return null;

  const canRemoveRow = contentLen > 1;

  return (
    <li
      ref={setNodeRef}
      style={sortableStyle}
      className={`rounded-2xl border-2 bg-[#FAFAFA] p-4 sm:p-5 ${
        isDragging
          ? "opacity-60 shadow-lg ring-2 ring-[#6366F1]/30"
          : "border-[#E5E5E5]"
      }`}
    >
      {canManage && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div
            className="flex h-9 w-7 cursor-grab touch-none items-center justify-center rounded-lg text-[#AFAFAF] hover:bg-[#F0F0F0] hover:text-[#777777] active:cursor-grabbing"
            aria-hidden
            {...(dragHandleProps ?? {})}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <button
            type="button"
            onClick={() => removeContentRow(sectionIndex, rowIndex)}
            disabled={!canRemoveRow}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border-2 border-[#FECACA] bg-[#FEF2F2] px-3 text-xs font-extrabold uppercase tracking-wide text-[#B91C1C] disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            Remove row
          </button>
        </div>
      )}
      <label className={`${faqLabelClass} mb-4`}>
        Subtitle
        <Input
          accent="teal"
          type="text"
          disabled={disabled}
          value={row.subTitle}
          onChange={(e) =>
            patchContentItem(sectionIndex, rowIndex, {
              subTitle: e.target.value,
            })
          }
          placeholder="Short heading"
        />
      </label>
      <label className={faqLabelClass}>
        Description
        <Textarea
          accent="teal"
          rows={4}
          disabled={disabled}
          value={row.description}
          onChange={(e) =>
            patchContentItem(sectionIndex, rowIndex, {
              description: e.target.value,
            })
          }
          placeholder="Full answer text"
        />
      </label>
    </li>
  );
};
